import { EditableText, FieldGroup, Subheading } from '@criteria-labs/react'
import type { Service } from '@criteria-labs/docs-kit'
import debounce from 'lodash.debounce'
import { useMemo } from 'react'
import { Section } from '../Section'
import { InspectorBody, InspectorPrimaryContent, InspectorSecondaryContent } from '../article'
import { UpdateContactEmail, UpdateContactName, UpdateContactURL } from '../changes/contact'
import {
  UpdateExternalDocumentationDescription,
  UpdateExternalDocumentationURL
} from '../changes/externaldocumentation'
import {
  UpdateInfoDescription,
  UpdateInfoSummary,
  UpdateInfoTermsOfService,
  UpdateInfoTitle,
  UpdateInfoVersion
} from '../changes/info'
import { UpdateLicenseName, UpdateLicenseURL } from '../changes/license'
import { InlineField, InlineFieldGroup, InlineLabel } from '../inline-fieldset'
import { useDispatchChange } from '../useDispatchChange'

export function ServiceOverview({ service }: { service: Service }) {
  const dispatchChange = useDispatchChange()
  const debouncedDispatchChange = useMemo(() => debounce(dispatchChange), [dispatchChange])

  return (
    <InspectorBody split>
      <InspectorPrimaryContent>
        <Section className="mt-6">
          <Subheading className="flex min-h-10 items-center">General Information</Subheading>
          <InlineFieldGroup className="mt-3">
            <InlineField>
              <InlineLabel>Title</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  placeholder="Add a title…"
                  className="text-sm"
                  value={service.openAPI.info.title}
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateInfoTitle(value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
            {service.openAPI.openapi.startsWith('3.1.') && (
              <InlineField>
                <InlineLabel>Summary</InlineLabel>
                <div data-slot="control" className="mt-0.5">
                  <EditableText
                    placeholder="Add a summary…"
                    className="text-sm"
                    value={'summary' in service.openAPI.info ? service.openAPI.info.summary : ''}
                    onChange={(value) => {
                      debouncedDispatchChange(new UpdateInfoSummary(value))
                    }}
                    onBlur={() => {
                      debouncedDispatchChange.flush()
                    }}
                  />
                </div>
              </InlineField>
            )}
            <InlineField>
              <InlineLabel>Version</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  placeholder="Add a version…"
                  className="text-sm"
                  value={service.openAPI.info.version}
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateInfoVersion(value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
            <InlineField>
              <InlineLabel>Description</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  multiline
                  placeholder="Add a description…"
                  className="text-sm"
                  value={service.openAPI.info.description}
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateInfoDescription(value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
          </InlineFieldGroup>
        </Section>
      </InspectorPrimaryContent>
      <InspectorSecondaryContent className="space-y-12 py-8">
        <Section>
          <Subheading className="flex min-h-10 items-center">Contact Details</Subheading>
          <InlineFieldGroup>
            <InlineField>
              <InlineLabel>Name</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={service.openAPI.info.contact?.name}
                  placeholder="Add a name…"
                  className="text-sm"
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateContactName(value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
            <InlineField>
              <InlineLabel>URL</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={service.openAPI.info.contact?.url}
                  placeholder="Add a URL…"
                  className="text-sm"
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateContactURL(value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
            <InlineField>
              <InlineLabel>Email</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={service.openAPI.info.contact?.email}
                  placeholder="Add an email…"
                  className="text-sm"
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateContactEmail(value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
          </InlineFieldGroup>
        </Section>
        <Section>
          <Subheading className="flex min-h-10 items-center">License Details</Subheading>
          <InlineFieldGroup>
            <InlineField>
              <InlineLabel>Name</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={service.openAPI.info.license?.name}
                  placeholder="Add a name…"
                  className="text-sm"
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateLicenseName(value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
            <InlineField>
              <InlineLabel>URL</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={service.openAPI.info.license?.url}
                  placeholder="Add a URL…"
                  className="text-sm"
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateLicenseURL(value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
          </InlineFieldGroup>
        </Section>
        <Section>
          <Subheading className="flex min-h-10 items-center">Terms of Service</Subheading>
          <FieldGroup className="mt-3">
            <InlineField>
              <InlineLabel>URL</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={service.openAPI.info.termsOfService}
                  placeholder="Add a URL…"
                  className="text-sm"
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateInfoTermsOfService(value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
          </FieldGroup>
        </Section>
        <Section>
          <Subheading className="flex min-h-10 items-center">External Documentation</Subheading>
          <InlineFieldGroup>
            <InlineField>
              <InlineLabel>URL</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={service.openAPI.externalDocs?.url}
                  placeholder="Add a URL…"
                  className="text-sm"
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateExternalDocumentationURL('#/externalDocs', value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
            <InlineField>
              <InlineLabel>Description</InlineLabel>
              <div data-slot="control" className="mt-0.5">
                <EditableText
                  value={service.openAPI.externalDocs?.description}
                  placeholder="Add a description…"
                  className="text-sm"
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateExternalDocumentationDescription('#/externalDocs', value))
                  }}
                  onBlur={() => {
                    debouncedDispatchChange.flush()
                  }}
                />
              </div>
            </InlineField>
          </InlineFieldGroup>
        </Section>
      </InspectorSecondaryContent>
    </InspectorBody>
  )
}
